import type { AncRegistrationDetails } from './clinic_registration.js';
// Pamosi Clinic — shared types.
//
// The clinic is the primary business unit for the Pamosi Clinic surface (see
// migration 0059). One clinic row per real facility, N clinicians linked via
// clinic_members. Admins can edit the clinic, mint invites, transfer admin
// role, and delete the clinic. Providers use the profile but can't manage it.
//
// Verification is background-async: registration returns a clinic with
// status='pending' and the mobile app polls / re-fetches on focus to reflect
// the reviewer's decision.

// ─── enums ────────────────────────────────────────────────────────────────

export type ClinicFacilityType =
    | 'private_hospital'
    | 'general_hospital'
    | 'maternity_womens_health'
    | 'pharmacy'
    | 'laboratory'
    | 'specialty_clinic';

export const CLINIC_FACILITY_TYPES: ClinicFacilityType[] = [
    'private_hospital',
    'general_hospital',
    'maternity_womens_health',
    'pharmacy',
    'laboratory',
    'specialty_clinic',
];

// Labels are rendered verbatim in the mobile picker.
export const CLINIC_FACILITY_TYPE_LABELS: Record<ClinicFacilityType, string> = {
    private_hospital: 'Private Hospital',
    general_hospital: 'General Hospital',
    maternity_womens_health: "Maternity / Women's Health",
    pharmacy: 'Pharmacy',
    laboratory: 'Laboratory',
    specialty_clinic: 'Specialty Clinic',
};

export type ClinicVerificationStatus =
    | 'pending'
    | 'verified'
    | 'needs_info'
    | 'rejected';

// V0 Week 6 — expanded from admin|provider to the full clinic split. See
// migration 0078. 'provider' remains in the enum for legacy rows only; the
// app never emits it anymore. New members always come in as one of the
// five explicit roles below.
export type ClinicMemberRole =
    | 'admin'
    | 'doctor'
    | 'nurse'
    | 'pharmacist'
    | 'lab_technician'
    | 'receptionist'
    | 'provider'; // legacy — deprecated, never picked in UI

// UI-facing roles (order matters — matches the invite picker layout).
export const CLINIC_MEMBER_ROLE_OPTIONS: Exclude<ClinicMemberRole, 'provider'>[] = [
    'admin',
    'doctor',
    'nurse',
    'pharmacist',
    'lab_technician',
    'receptionist',
];

export const CLINIC_MEMBER_ROLE_LABELS: Record<ClinicMemberRole, string> = {
    admin: 'Admin',
    doctor: 'Doctor',
    nurse: 'Nurse',
    pharmacist: 'Pharmacist',
    lab_technician: 'Lab technician',
    receptionist: 'Receptionist',
    provider: 'Provider (legacy)',
};

// ─── row shapes ───────────────────────────────────────────────────────────

export interface Clinic {
    id: string;
    facility_name: string;
    facility_type: ClinicFacilityType;
    business_phone_e164: string | null;
    cac_rc_number: string | null;
    license_number: string | null;
    // Workspace-level location + contact (migration 0084). All nullable
    // during rollout — old rows won't have them; the new pre-auth register
    // form always populates them.
    workspace_email: string | null;
    physical_address: string | null;
    country: string | null;
    state: string | null;
    lga: string | null;
    verification_status: ClinicVerificationStatus;
    verified_at: string | null;
    verification_notes: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

export interface ClinicMember {
    clinic_id: string;
    clinician_id: string;
    role: ClinicMemberRole;
    created_at: string;
    created_by_clinician_id: string | null;
    revoked_at: string | null;
}

// Team hub row — includes the clinician's display name + phone so the admin
// can render the list without a second roundtrip.
export interface ClinicMemberWithClinician {
    member: ClinicMember;
    clinician_name: string | null;
    phone_e164: string;
}

export interface ClinicInvite {
    id: string;
    clinic_id: string;
    code: string;
    name_hint: string | null;
    role: ClinicMemberRole;  // Week 6 — admin picks target role at invite time
    expires_at: string;
    redeemed_at: string | null;
    redeemed_by_clinician_id: string | null;
    created_at: string;
    created_by_clinician_id: string | null;
}

// Admin-facing list — hides the code once redeemed so the UI doesn't
// accidentally re-share a dead code.
export interface ClinicInviteListItem {
    id: string;
    code: string | null; // null after redemption
    name_hint: string | null;
    role: ClinicMemberRole;
    expires_at: string;
    redeemed_at: string | null;
    created_at: string;
    status: 'pending' | 'redeemed' | 'expired';
}

// ─── request / response payloads ──────────────────────────────────────────

export interface RegisterClinicReq {
    // Admin identity captured during OTP sign-in is separate; the clinician
    // row is created before this call runs. `full_name` is captured here to
    // update the clinician row if it's blank. `admin_email` is stored on the
    // clinicians row and is now required by the pre-auth registration form.
    full_name: string;
    admin_email: string;
    facility_name: string;
    facility_type: ClinicFacilityType;
    business_phone_e164: string;
    // CAC / RC number was required in v1 but is now optional (many small
    // facilities operate under a proprietor's own name). Verification skips
    // the CAC leg when absent and routes the clinic directly into manual
    // license review.
    cac_rc_number?: string;
    license_number: string;
    // New workspace fields (migration 0084). Required by the pre-auth form
    // and by the updated authed RegisterPane; the API still accepts requests
    // without them for backward-compat with any older clients in flight.
    workspace_email: string;
    physical_address: string;
    country: string;   // ISO-3166 alpha-2, uppercase — 'NG' for v1
    state: string;
    lga: string;
}
export interface RegisterClinicRes {
    clinic: Clinic;
    // The admin's membership row, so the mobile app can immediately show the
    // Team Hub without a second call.
    member: ClinicMember;
}

export interface JoinClinicReq {
    // Same rationale as RegisterClinicReq.full_name — populates clinicians
    // row if it's still blank at redemption time.
    full_name?: string;
    code: string;
}
export interface JoinClinicRes {
    clinic: Clinic;
    member: ClinicMember;
}

export interface ListClinicMembersRes {
    members: ClinicMemberWithClinician[];
}

export interface CreateClinicInviteReq {
    name_hint?: string | null;
    // Week 6 — required for new invites. Server rejects 'provider' (legacy)
    // and only accepts one of CLINIC_MEMBER_ROLE_OPTIONS.
    role: Exclude<ClinicMemberRole, 'provider'>;
}
export interface CreateClinicInviteRes {
    invite: ClinicInvite;
    // Pre-built message the admin can paste into WhatsApp / SMS.
    share_text: string;
}

export interface ListClinicInvitesRes {
    invites: ClinicInviteListItem[];
}

// Admin-only mutations.

export interface UpdateClinicReq {
    facility_name?: string;
    facility_type?: ClinicFacilityType;
    business_phone_e164?: string;
    cac_rc_number?: string;
    license_number?: string;
    workspace_email?: string;
    physical_address?: string;
    country?: string;
    state?: string;
    lga?: string;
}
export type UpdateClinicRes = Clinic;

export interface TransferAdminReq {
    to_clinician_id: string;
}
// Returns the updated member rows for both parties.
export interface TransferAdminRes {
    old_admin: ClinicMember;
    new_admin: ClinicMember;
}

// Fetched by any member — used to render Team Hub / dashboard.
export interface GetMyClinicRes {
    clinic: Clinic | null;
    member: ClinicMember | null;
}

// ─── reception / card types (Phase 1a + 1b, 2026-10-03) ───────────────────
//
// See migrations 0093 + 0094. The reception module digitises the clinic's
// paper card types and the Section A–H registration flow. These types are
// shared verbatim between the API router (clinician/reception_router.ts)
// and the web-clinic UI (web-clinic/src/routes/PersonalCardScreen.tsx).

export type PatientCardType =
    | 'personal'
    | 'family'
    | 'corporate'
    | 'gynae'
    | 'anc'
    | 'emergency';

export const PATIENT_CARD_TYPES: PatientCardType[] = [
    'personal', 'family', 'corporate', 'gynae', 'anc', 'emergency',
];

export const PATIENT_CARD_TYPE_LABELS: Record<PatientCardType, string> = {
    personal:  'Personal card',
    family:    'Family card',
    corporate: 'Corporate card',
    gynae:     'Gynae card',
    anc:       'ANC / Maternity card',
    emergency: 'Emergency card',
};

export type PatientCardStatus = 'active' | 'inactive' | 'archived';

export type HmoVerificationStatus =
    | 'unverified'
    | 'pending'
    | 'verified'
    | 'rejected';

export type RegGender =
    | 'male' | 'female' | 'other' | 'prefer_not_to_say' | 'unknown';
export type RegMaritalStatus =
    | 'single' | 'married' | 'divorced' | 'widowed' | 'separated' | 'unknown';
export type RegSmoking = 'never' | 'former' | 'current' | 'unknown';
export type RegAlcohol = 'never' | 'occasional' | 'regular' | 'unknown';
export type RegLivingSituation =
    | 'alone' | 'with_family' | 'caregiver' | 'other' | 'unknown';
export type RegAllergyStatus =
    | 'not_assessed' | 'none_known' | 'recorded' | 'unknown';
export type RegBloodGroupAbo = 'a' | 'b' | 'ab' | 'o' | 'unknown';
export type RegRhFactor = 'positive' | 'negative' | 'unknown';
export type RegGenotype =
    | 'aa' | 'as' | 'ss' | 'ac' | 'sc' | 'other' | 'unknown';

// Section keys used by patient_registration_details.completed_sections.
// Mirrors the clinic feedback's A–H labels; H lives on patient_hmo so it is
// NOT part of this array (completeness of HMO is tracked via patient_hmo
// rows). G lives on the visit/encounter, likewise excluded.
export type RegSectionKey =
    | 'personal'       // A
    | 'contact'        // B
    | 'socio'          // C
    | 'social'         // D
    | 'medical'        // E
    | 'nok';           // F

export const REG_SECTION_KEYS: RegSectionKey[] = [
    'personal', 'contact', 'socio', 'social', 'medical', 'nok',
];

export interface HmoProvider {
    id: string;
    slug: string;
    display_name: string;
    short_name: string | null;
    is_active: boolean;
    created_by_clinic_id: string | null;
}

export interface PatientCard {
    id: string;
    patient_id: string;
    clinic_id: string;
    card_type: PatientCardType;
    hospital_number: string | null;
    family_id: string | null;
    status: PatientCardStatus;
    opened_at: string;
    closed_at: string | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface PatientRegistrationDetails {
    anc_details?: AncRegistrationDetails | null;
    id: string;
    patient_id: string;
    clinic_id: string;

    // Section A
    dob: string | null;
    estimated_age_years: number | null;
    estimated_age_as_of: string | null;
    gender: RegGender | null;
    clinical_sex: string | null;
    marital_status: RegMaritalStatus | null;

    // Section B
    address: string | null;
    phone_e164_primary: string | null;
    phone_e164_alt: string | null;
    email: string | null;
    preferred_contact_channel: string | null;

    // Section C
    occupation: string | null;
    state_of_origin: string | null;
    ethnicity: string | null;
    religion: string | null;

    // Section D
    smoking: RegSmoking | null;
    alcohol: RegAlcohol | null;
    recreational_drugs: boolean | null;
    recreational_drugs_detail: string | null;
    living_situation: RegLivingSituation | null;
    living_situation_detail: string | null;

    // Section E
    genotype: RegGenotype | null;
    blood_group_abo: RegBloodGroupAbo | null;
    blood_group_rh: RegRhFactor | null;
    allergy_status: RegAllergyStatus | null;
    allergies_detail: string | null;
    chronic_conditions_detail: string | null;
    current_medicines_detail: string | null;
    surgical_history_detail: string | null;

    // Section F
    nok_name: string | null;
    nok_phone_e164: string | null;
    nok_relationship: string | null;
    nok_address: string | null;
    alt_emergency_contact_name: string | null;
    alt_emergency_contact_phone: string | null;

    is_draft: boolean;
    completed_sections: RegSectionKey[];
    created_at: string;
    updated_at: string;
}

export interface PatientHmoCoverage {
    id: string;
    patient_id: string;
    clinic_id: string;
    hmo_provider_id: string;
    policy_number: string | null;
    plan_name: string | null;
    member_reference: string | null;
    verification_status: HmoVerificationStatus;
    authorization_reference: string | null;
    is_active: boolean;
    notes: string | null;
    verified_at: string | null;
    verified_by_clinician_id: string | null;
    created_at: string;
    updated_at: string;
}

// Request body for PATCH /reception/patients/:patientId/hmo/:hmoId/verify.
// status moves verification_status; only 'verified' or 'rejected' are
// accepted (the row defaults to 'unverified' and reception flips to
// 'pending' when they first dial the payer — represented client-side by
// patching with status='pending' + no auth ref).
export interface VerifyHmoCoverageRequest {
    status: 'pending' | 'verified' | 'rejected';
    authorization_reference?: string | null;
    notes?: string | null;
}

// Lightweight patient view used by reception search results. Contains only
// the fields reception needs to disambiguate duplicates; the full file is
// fetched separately once a hit is chosen.
export interface ReceptionPatientHit {
    patient_id: string;
    full_name: string | null;
    phone_e164: string;
    dob: string | null;
    estimated_age_years: number | null;
    gender: RegGender | null;
    hospital_numbers: string[];       // across all active cards at this clinic
    active_card_types: PatientCardType[];
    identity_tier: string;
}

// ─── reception request / response payloads ──────────────────────────────

export interface ReceptionPatientSearchRes {
    hits: ReceptionPatientHit[];
    // Server-side flag: at least one hit is a strong duplicate candidate
    // (exact phone match, exact hospital_number match, or exact name + DOB).
    // Front desk uses this to show the "Possible duplicate" banner before
    // offering Create New.
    has_strong_match: boolean;
}

export interface ReceptionCreatePatientReq {
    // Required — reception always captures a phone (even for an estimated
    // walk-in, a shared family phone is accepted). Mobile Health identity
    // joins on phone_e164 so this stays mandatory.
    phone_e164: string;
    full_name?: string;
    card_type: PatientCardType;
    // Hospital number is optional at create — some clinics assign it on
    // completion of the form, others keep paper numbers. Unique per clinic
    // when set (enforced in DB).
    hospital_number?: string;
    // For card_type='family', the family_id this patient should be joined
    // under. Required when card_type='family'; ignored otherwise.
    family_id?: string;
    // Relationship within the family (free text, e.g. "spouse", "child").
    family_relationship?: string;
    family_is_account_holder?: boolean;
    // Freeform notes stored on patient_cards.notes. Corporate card uses
    // this to capture employer_name + staff_reference (serialised plainly,
    // e.g. "Employer: Acme | Staff Ref: 12345") until a schema migration
    // promotes those to typed columns.
    notes?: string;
    // Server-side: creates patient (via existing findOrCreatePatientByPhone,
    // tagged signup_source='clinic_walkin'), card, and a draft registration
    // details row. Idempotent: resubmitting with the same phone returns the
    // existing patient + their existing card of that type if any.
}

export interface ReceptionCreatePatientRes {
    patient: {
        id: string;
        phone_e164: string;
        full_name: string | null;
        identity_tier: string;
    };
    card: PatientCard;
    registration: PatientRegistrationDetails;
    hmo_coverages: PatientHmoCoverage[];
    is_new_patient: boolean;
    is_new_card: boolean;
}

// Full registration bundle for a (patient, clinic): card + details + HMO
// coverages. Returned by GET /reception/patients/:id/registration.
export interface ReceptionRegistrationBundleRes {
    patient: {
        id: string;
        phone_e164: string;
        full_name: string | null;
        identity_tier: string;
    };
    cards: PatientCard[];
    registration: PatientRegistrationDetails | null;
    hmo_coverages: PatientHmoCoverage[];
}

// Section A–F field updater. All fields optional — partial updates allowed.
// Section G lives on the encounter, Section H on patient_hmo (separate
// endpoint). completed_sections is server-managed via the explicit
// mark_section_complete / mark_section_incomplete booleans.
export interface ReceptionUpdateRegistrationReq {
    anc_details?: AncRegistrationDetails | null;
    // A
    dob?: string | null;
    estimated_age_years?: number | null;
    estimated_age_as_of?: string | null;
    gender?: RegGender | null;
    clinical_sex?: string | null;
    marital_status?: RegMaritalStatus | null;

    // B
    address?: string | null;
    phone_e164_primary?: string | null;
    phone_e164_alt?: string | null;
    email?: string | null;
    preferred_contact_channel?: string | null;

    // C
    occupation?: string | null;
    state_of_origin?: string | null;
    ethnicity?: string | null;
    religion?: string | null;

    // D
    smoking?: RegSmoking | null;
    alcohol?: RegAlcohol | null;
    recreational_drugs?: boolean | null;
    recreational_drugs_detail?: string | null;
    living_situation?: RegLivingSituation | null;
    living_situation_detail?: string | null;

    // E
    genotype?: RegGenotype | null;
    blood_group_abo?: RegBloodGroupAbo | null;
    blood_group_rh?: RegRhFactor | null;
    allergy_status?: RegAllergyStatus | null;
    allergies_detail?: string | null;
    chronic_conditions_detail?: string | null;
    current_medicines_detail?: string | null;
    surgical_history_detail?: string | null;

    // F
    nok_name?: string | null;
    nok_phone_e164?: string | null;
    nok_relationship?: string | null;
    nok_address?: string | null;
    alt_emergency_contact_name?: string | null;
    alt_emergency_contact_phone?: string | null;

    // Section completion toggles. If a section's key appears here, it is
    // added to (true) or removed from (false) completed_sections.
    section_marks?: Partial<Record<RegSectionKey, boolean>>;

    // Flip is_draft to false. Server also verifies all 6 sections are in
    // completed_sections before honouring this — otherwise returns 400.
    submit?: boolean;
}

export type ReceptionUpdateRegistrationRes = PatientRegistrationDetails;

// HMO list for the reception picker. Returns active providers; search is
// case-insensitive display_name / short_name match.
export interface ReceptionHmoProvidersRes {
    providers: HmoProvider[];
}

export interface ReceptionUpsertHmoReq {
    hmo_provider_id: string;
    policy_number?: string;
    plan_name?: string;
    member_reference?: string;
    authorization_reference?: string;
    notes?: string;
    // Verification is a separate flow (requires evidence); defaults to
    // 'unverified' on first write. Reception may NOT set it to 'verified'.
}

export type ReceptionUpsertHmoRes = PatientHmoCoverage;

// ─── Family card (Phase 1c, 2026-10-03) ──────────────────────────────────
//
// A family card groups patients (beneficiaries) under a shared account for
// billing + shared contact. Clinical history remains per-patient — the
// family link never grants the account holder access to another member's
// chart. See migration 0093 (patient_families, family_beneficiaries).

export interface PatientFamily {
    id: string;
    clinic_id: string;
    account_number: string | null;
    family_name: string;
    address: string | null;
    main_contact_name: string | null;
    main_contact_phone_e164: string | null;
    main_contact_email: string | null;
    default_hmo_provider_id: string | null;
    created_at: string;
    updated_at: string;
}

export interface FamilyBeneficiary {
    id: string;
    family_id: string;
    patient_id: string;
    clinic_id: string;
    relationship: string | null;
    is_account_holder: boolean;
    uses_own_contact: boolean;
    created_at: string;
    updated_at: string;
    // Hydrated server-side from patients + patient_registration_details for
    // the beneficiaries list view.
    patient_full_name: string | null;
    patient_phone_e164: string;
    patient_dob: string | null;
}

// Family search hit — mirrors patient search but at the family level.
export interface ReceptionFamilyHit {
    family_id: string;
    family_name: string;
    account_number: string | null;
    main_contact_phone_e164: string | null;
    beneficiary_count: number;
}

export interface ReceptionFamilySearchRes {
    hits: ReceptionFamilyHit[];
    has_strong_match: boolean;
}

export interface ReceptionCreateFamilyReq {
    family_name: string;
    account_number?: string;
    address?: string;
    main_contact_name?: string;
    main_contact_phone_e164?: string;
    main_contact_email?: string;
    default_hmo_provider_id?: string;
}

export interface ReceptionCreateFamilyRes {
    family: PatientFamily;
}

export interface ReceptionFamilyBundleRes {
    family: PatientFamily;
    beneficiaries: FamilyBeneficiary[];
}

export interface ReceptionAddBeneficiaryReq {
    // Add an existing patient (looked up by phone) or create one. Mirrors
    // the Personal card create shape minus card_type (always 'family' here).
    phone_e164: string;
    full_name?: string;
    relationship?: string;
    is_account_holder?: boolean;
    uses_own_contact?: boolean;
}

export interface ReceptionAddBeneficiaryRes {
    beneficiary: FamilyBeneficiary;
    card: PatientCard;
    is_new_patient: boolean;
    is_new_beneficiary: boolean;
}

// ─── Phase 2a: departments + visit queue (2026-10-03) ────────────────────
//
// See migration 0095. After reception creates/finds a card, they "Send to
// department" — this inserts a visit_queue row that clinicians at that
// dept pick up. Shared verbatim between reception_router / queues_router
// and the clinic UI.

export type ClinicDepartmentKind =
    | 'triage'
    | 'consult'
    | 'lab'
    | 'pharmacy'
    | 'ancillary';

export const CLINIC_DEPARTMENT_KIND_LABELS: Record<ClinicDepartmentKind, string> = {
    triage:    'Triage',
    consult:   'Consultation',
    lab:       'Lab',
    pharmacy:  'Pharmacy',
    ancillary: 'Ancillary',
};

export interface ClinicDepartment {
    id: string;
    clinic_id: string;
    kind: ClinicDepartmentKind;
    name: string;
    short_name: string | null;
    is_active: boolean;
    display_order: number;
    created_at: string;
    updated_at: string;
}

export type VisitQueueStatus =
    | 'waiting'
    | 'in_progress'
    | 'done'
    | 'cancelled'
    | 'rerouted';

export interface VisitQueueEntry {
    id: string;
    clinic_id: string;
    department_id: string;
    patient_id: string;
    patient_card_id: string | null;
    encounter_id: string | null;
    status: VisitQueueStatus;
    chief_complaint: string | null;
    priority: number;
    enqueued_by_clinician_id: string;
    enqueued_at: string;
    picked_up_at: string | null;
    completed_at: string | null;
    created_at: string;
    updated_at: string;
}

// Hydrated server-side for the queue list view (joins patient + card so
// the queue chip can show "Jane Doe · ANC · #PH-123" without a round-trip).
export interface VisitQueueEntryView extends VisitQueueEntry {
    patient_full_name: string | null;
    patient_phone_e164: string;
    card_type: PatientCardType | null;
    hospital_number: string | null;
    department_name: string;
    department_short_name: string | null;
    department_kind: ClinicDepartmentKind;
}

export interface ReceptionDepartmentsRes {
    departments: ClinicDepartment[];
}

export interface ReceptionEnqueueReq {
    department_id: string;
    patient_card_id?: string;
    chief_complaint?: string;
    priority?: number;
}

export interface ReceptionEnqueueRes {
    entry: VisitQueueEntry;
}

export interface QueueListRes {
    entries: VisitQueueEntryView[];
}

export interface QueueUpdateStatusReq {
    status: VisitQueueStatus;
    // Required when status='in_progress' (ties the queue entry to the
    // encounter the clinician just created). Ignored otherwise.
    encounter_id?: string;
}

export type QueueUpdateStatusRes = VisitQueueEntry;

// ─── Patient shared instructions (Phase 2b) ──────────────────────────────
//
// Staff-to-staff notes about a patient that live outside any single encounter
// (see migration 0096). Any clinic staff with patient access reads + writes;
// author or admin may edit the body; anyone may pin, unpin, resolve, or
// unresolve.

export interface PatientSharedInstruction {
    id: string;
    clinic_id: string;
    patient_id: string;
    author_clinician_id: string | null;
    body: string;
    pinned: boolean;
    resolved_at: string | null;
    resolved_by_clinician_id: string | null;
    created_at: string;
    updated_at: string;
}

// List-view hydration: author name shown next to the note so staff know
// who to ask follow-up questions of. Author may be null (clinician record
// soft-deleted) — UI falls back to "Unknown".
export interface PatientSharedInstructionView extends PatientSharedInstruction {
    author_full_name: string | null;
    resolved_by_full_name: string | null;
}

export interface SharedInstructionsListRes {
    instructions: PatientSharedInstructionView[];
}

export interface SharedInstructionCreateReq {
    body: string;
    pinned?: boolean;
}

export interface SharedInstructionUpdateReq {
    body?: string;
    pinned?: boolean;
    resolved?: boolean;
}

export interface SharedInstructionRes {
    instruction: PatientSharedInstructionView;
}

// ─── Mentions / inbox (Phase 2c) ─────────────────────────────────────────
//
// @username tags inside an instruction body create instruction_mentions
// rows (migration 0097). The mentioned clinician sees the mention in their
// inbox badge until they clear it. See the DB helper for the parsing rules.

export interface InstructionMention {
    id: string;
    clinic_id: string;
    instruction_id: string;
    mentioned_clinician_id: string;
    created_by_clinician_id: string | null;
    created_at: string;
    read_at: string | null;
    cleared_at: string | null;
}

// Inbox view — hydrated with just enough context to render a card without
// a round-trip: who tagged me, which patient, and a trimmed snippet of
// the instruction body. UI opens the parent patient surface for the
// full note + resolve controls.
export interface InboxMentionEntry {
    id: string;                                 // mention id (used for clear)
    instruction_id: string;
    patient_id: string;
    patient_full_name: string | null;
    patient_phone_e164: string | null;
    author_full_name: string | null;
    body_snippet: string;                       // first ~140 chars of body
    pinned: boolean;
    instruction_resolved_at: string | null;
    created_at: string;
    read_at: string | null;
}

export interface InboxListRes {
    entries: InboxMentionEntry[];
    unread_count: number;
}

// Clinic member picker payload for the mention autocomplete. We expose
// username + display name only — the UI never needs the raw phone/email
// to render a mention chip, and this keeps the roster cheap to ship.
export interface MentionCandidate {
    clinician_id: string;
    username: string | null;
    full_name: string | null;
    role: ClinicMemberRole;
}

export interface MentionCandidatesRes {
    candidates: MentionCandidate[];
}

// ─── Phase 2d — nurse triage portal (2026-10-03) ─────────────────────────

export type TriageAcuity = 'routine' | 'urgent' | 'emergency';

export const TRIAGE_ACUITY_OPTIONS: readonly TriageAcuity[] = [
    'routine',
    'urgent',
    'emergency',
] as const;

// Raw DB row for an encounter_triage entry. One-to-one with encounter via
// unique(encounter_id); the nurse re-opens this same row for re-triage
// rather than inserting a second one.
export interface EncounterTriage {
    id: string;
    clinic_id: string;
    encounter_id: string;
    nurse_clinician_id: string;
    bp_systolic: number | null;
    bp_diastolic: number | null;
    heart_rate: number | null;
    resp_rate: number | null;
    temp_c: number | null;
    spo2: number | null;
    weight_kg: number | null;
    height_cm: number | null;
    pain_score: number | null;
    chief_complaint: string | null;
    acuity: TriageAcuity;
    triage_note: string | null;
    started_at: string;
    completed_at: string | null;
    created_at: string;
    updated_at: string;
}

// Hydrated view — the nurse's display name is attached so the encounter
// card can render "Triage: Nurse Bola · 09:12" without a round-trip.
export interface EncounterTriageView extends EncounterTriage {
    nurse_full_name: string | null;
}

export interface TriageRes {
    triage: EncounterTriageView;
}

// Create/update payload. Everything but acuity is optional; acuity defaults
// to 'routine' server-side on first open.
export interface TriageUpsertReq {
    bp_systolic?: number | null;
    bp_diastolic?: number | null;
    heart_rate?: number | null;
    resp_rate?: number | null;
    temp_c?: number | null;
    spo2?: number | null;
    weight_kg?: number | null;
    height_cm?: number | null;
    pain_score?: number | null;
    chief_complaint?: string | null;
    acuity?: TriageAcuity;
    triage_note?: string | null;
    // When true, mark completed_at = now() and (server) re-queue the
    // encounter to the clinical department. Setting to false re-opens.
    complete?: boolean;
    // Required on `complete: true` — which clinical department to route
    // the encounter to next. Rejected when `complete` is not true.
    next_department_id?: string;
}

// ─── Procedure fulfillments (Phase 2e, 2026-10-03) ────────────────────────
//
// A service_request can be fulfilled N times — e.g. a 3-day wound dressing
// is one request with 3 fulfillment rows. Written by nurses on the
// Procedures Queue; read by doctors on the encounter detail.
//
// See migration 0099. photo_object_key is reserved for Phase 2e.1 (image
// upload) — API accepts it but web-clinic v1 doesn't surface photo capture.

export interface ServiceRequestFulfillment {
    id: string;
    service_request_id: string;
    clinic_id: string;
    patient_id: string;
    fulfilled_by_clinician_id: string;
    performed_at: string;
    note: string | null;
    dose_text: string | null;
    batch_number: string | null;
    site: string | null;
    created_at: string;
    updated_at: string;
}

// One photo attached to a procedure fulfillment (Phase 3 — multi-photo).
// Replaces the single photo_object_key column on fulfillments. The
// signed_url is a short-lived (7d) GET URL minted at list time; it is
// null when the sign call failed, in which case the UI falls back to no
// thumbnail rather than erroring the row.
export interface FulfillmentPhotoView {
    id: string;
    fulfillment_id: string;
    uploaded_at: string;
    uploaded_by_clinician_id: string;
    uploaded_by_full_name: string | null;
    content_type: string;
    signed_url: string | null;
}

// Hydrated for the Procedures Queue card so we can render
// "Nurse Bola · 09:12 · site: left arm" without a round-trip.
export interface ServiceRequestFulfillmentView extends ServiceRequestFulfillment {
    fulfilled_by_full_name: string | null;
    // Phase 3 — N photos per fulfillment. Empty array when no photos
    // attached. Each entry carries its own signed URL (see above).
    photos: FulfillmentPhotoView[];
}

// Nurse's queue card — joins the open service_request to patient / card
// / encounter data so the row renders complete.
export interface ProceduresQueueEntry {
    service_request_id: string;
    description: string;
    code: string | null;
    status: 'requested' | 'in_progress' | 'fulfilled' | 'cancelled';
    requested_at: string;
    fulfilled_at: string | null;
    cancelled_at: string | null;
    cancelled_reason: string | null;
    // Encounter / patient context denormalised onto the queue row for the
    // card. All joined server-side so one query renders a row.
    encounter_id: string;
    patient_id: string;
    patient_full_name: string | null;
    patient_phone_e164: string | null;
    hospital_number: string | null;
    // Acuity is inherited from encounter_triage when present — lets us
    // surface emergency procedures at the top of the nurse's queue.
    acuity: TriageAcuity | null;
    // Number of fulfillments logged so far (so the card shows "2 of 3").
    fulfillment_count: number;
    requested_by_clinician_id: string | null;
    requested_by_full_name: string | null;
}

export interface ProceduresQueueRes {
    entries: ProceduresQueueEntry[];
}

// Logging a single execution. All fields except performed_at (defaulted
// server-side when omitted) are optional — a nurse may log "nebulisation
// completed, no issues" with no dose/batch.
export interface ServiceRequestFulfillmentReq {
    performed_at?: string;        // ISO; defaults to now() server-side
    note?: string | null;
    dose_text?: string | null;
    batch_number?: string | null;
    site?: string | null;
}

export interface ServiceRequestFulfillmentRes {
    fulfillment: ServiceRequestFulfillmentView;
}

export interface ServiceRequestFulfillmentsListRes {
    fulfillments: ServiceRequestFulfillmentView[];
}

// Close the request: no more fulfillments expected.
export interface ServiceRequestCloseReq {
    fulfilled_notes?: string | null;
}

// Cancel the request (doctor-side, or admin override).
export interface ServiceRequestCancelReq {
    reason?: string | null;
}

// Phase 3 — direct-to-Storage multi-photo upload for a fulfillment.
// Flow: client POSTs sign-upload → gets a photo_id, a signed PUT URL +
// storage path, uploads the compressed JPEG straight to Supabase Storage,
// then POSTs attach(photo_id) to flip the row from pending to live.
// Keeps the Worker out of the bytes path (CPU budget) and avoids base64
// bloat. One row per photo (see migration 0100).
export interface ProcedurePhotoSignUploadReq {
    content_type: string;     // 'image/jpeg' | 'image/png' | 'image/webp'
}

export interface ProcedurePhotoSignUploadRes {
    photo_id: string;         // row id — client passes this to attach + delete
    storage_path: string;     // what the server stored for this row
    signed_url: string;       // absolute PUT target
    expires_at: string;       // ISO; conservatively 90min from now
}

export interface ProcedurePhotoAttachRes {
    photo: FulfillmentPhotoView;
}

// Procedures queue filter options (Phase 3 UI toolbar: Hide mine, Show closed).
// API already supported include_closed; hide_mine is new.
export interface ProceduresQueueRequest {
    include_closed?: boolean;
    hide_mine?: boolean;
}

// ─── Admissions (Phase 3, 2026-10-03) ────────────────────────────────────
//
// See migration 0100. Admission is the "patient is in the clinic under
// observation right now" primitive — a dedicated lifecycle distinct from
// the clinical encounter. Nurses manage the admissions board; doctors
// (or admin) discharge; nurse mis-taps can self-recover via cancel.

export type AdmissionStatus = 'observing' | 'discharged' | 'cancelled';

export type AdmissionDisposition =
    | 'home'
    | 'escalated_to_hospital'
    | 'left_against_advice'
    | 'deceased';

export const ADMISSION_DISPOSITION_LABELS: Record<AdmissionDisposition, string> = {
    home:                   'Discharged home',
    escalated_to_hospital:  'Escalated to hospital',
    left_against_advice:    'Left against advice',
    deceased:               'Deceased',
};

export type AdmissionSource = 'post_consult' | 'direct';

export interface ClinicAdmission {
    id: string;
    clinic_id: string;
    patient_id: string;
    encounter_id: string;
    source: AdmissionSource;
    status: AdmissionStatus;
    location_label: string | null;
    reason: string | null;
    vitals_interval_minutes: number | null;
    last_vitals_observed_at: string | null;
    next_vitals_due_at: string | null;
    expected_duration_minutes: number | null;
    admitted_at: string;
    admitted_by_clinician_id: string;
    discharged_at: string | null;
    discharged_by_clinician_id: string | null;
    disposition: AdmissionDisposition | null;
    discharge_note: string | null;
    cancelled_at: string | null;
    cancelled_by_clinician_id: string | null;
    cancel_reason: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

// Hydrated for the admissions board — joins patient + card + admitter +
// most-recent-observation so a card renders with one query.
export interface AdmissionView extends ClinicAdmission {
    patient_full_name: string | null;
    patient_phone_e164: string | null;
    hospital_number: string | null;
    admitted_by_full_name: string | null;
    discharged_by_full_name: string | null;
    // Latest vital snapshot for the board chip. Each may be null if the
    // nurse hasn't captured it yet.
    latest_bp_systolic: number | null;
    latest_bp_diastolic: number | null;
    latest_temp_c: number | null;
    latest_spo2: number | null;
    latest_vitals_observed_at: string | null;
}

export interface AdmitRequest {
    encounter_id: string;
    source: AdmissionSource;
    reason?: string | null;
    location_label?: string | null;
    vitals_interval_minutes?: number | null;
    expected_duration_minutes?: number | null;
}

export interface AdmitResponse {
    admission: AdmissionView;
    is_new: boolean;
}

export interface DischargeRequest {
    disposition: AdmissionDisposition;
    note?: string | null;
}

export interface CancelAdmissionRequest {
    reason?: string | null;
}

export interface SetVitalsScheduleRequest {
    vitals_interval_minutes: number | null;
}

export interface AdmissionsListRes {
    admissions: AdmissionView[];
}

export interface AdmissionRes {
    admission: AdmissionView;
}

// ─── Phase 4 — inpatient basics (2026-10-04) ─────────────────────────────
//
// See migration 0105. Three features ship together:
//   * Observation chart — reuses existing Observation rows; filtered to a
//     single admission via its encounter_id. No new table.
//   * Shift reports — handover note per shift.
//   * MAR (Medication Administration Record) — per-dose nurse log.

// Observation chart point — lean projection of the Observation row used
// by the chart modal. Only the fields the chart actually renders.
export interface ObservationChartPoint {
    id: string;
    code: string;              // e.g. 'systolic_bp', 'temp_c'
    value_numeric: number | null;
    value_text: string | null;
    unit: string | null;
    observed_at: string;
    observed_by_full_name: string | null;
}

export interface AdmissionObservationChartRes {
    admission_id: string;
    encounter_id: string;
    points: ObservationChartPoint[];
}

// ─── Shift reports ───────────────────────────────────────────────────────

export interface ShiftReport {
    id: string;
    clinic_id: string;
    author_clinician_id: string;
    shift_label: string;
    shift_start_at: string | null;
    shift_end_at: string | null;
    summary: string;
    patient_ids_snapshot: string[];
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

// Hydrated for the list view — author name attached so the inbox-style
// row renders without a round-trip.
export interface ShiftReportView extends ShiftReport {
    author_full_name: string | null;
}

export interface CreateShiftReportRequest {
    shift_label: string;
    shift_start_at?: string | null;
    shift_end_at?: string | null;
    summary: string;
    patient_ids_snapshot?: string[];
}

export interface UpdateShiftReportRequest {
    shift_label?: string;
    shift_start_at?: string | null;
    shift_end_at?: string | null;
    summary?: string;
    patient_ids_snapshot?: string[];
}

export interface ShiftReportsListRes {
    reports: ShiftReportView[];
}

export interface ShiftReportRes {
    report: ShiftReportView;
}

// ─── Medication Administration Record (MAR) ──────────────────────────────

export type MedicationAdministrationStatus =
    | 'given'
    | 'held'
    | 'missed'
    | 'refused';

export const MEDICATION_ADMINISTRATION_STATUS_LABELS: Record<MedicationAdministrationStatus, string> = {
    given:   'Given',
    held:    'Held',
    missed:  'Missed',
    refused: 'Refused',
};

export interface MedicationAdministration {
    id: string;
    clinic_id: string;
    medication_request_id: string;
    admission_id: string | null;
    administered_by_clinician_id: string;
    scheduled_for: string | null;
    administered_at: string;
    status: MedicationAdministrationStatus;
    dose_text: string | null;
    note: string | null;
    created_at: string;
    superseded_by_id: string | null;
    deleted_at: string | null;
}

// Hydrated for the MAR panel — clinician name + the drug name from the
// parent medication_request so the row renders standalone.
export interface MedicationAdministrationView extends MedicationAdministration {
    administered_by_full_name: string | null;
    medication_name: string;
    medication_dose: string | null;
    medication_route: string | null;
    medication_frequency: string | null;
}

// Rendered inside the AdmissionsBoard — one entry per
// medication_request on the admission's encounter, with the recent
// administrations hanging off it.
export interface MarRequestLine {
    medication_request_id: string;
    medication_name: string;
    dose: string | null;
    route: string | null;
    frequency: string | null;
    status: string;                 // the parent script's lifecycle
    prescribed_at: string;
    administrations: MedicationAdministrationView[];
}

export interface AdmissionMarRes {
    admission_id: string;
    encounter_id: string;
    lines: MarRequestLine[];
}

export interface RecordAdministrationRequest {
    medication_request_id: string;
    status: MedicationAdministrationStatus;
    scheduled_for?: string | null;
    administered_at?: string | null;      // defaults to now()
    dose_text?: string | null;
    note?: string | null;
}

export interface AdministrationRes {
    administration: MedicationAdministrationView;
}
